import { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { VoidFn } from 'types/baseTypes';
import { CircleCloseIcon, DeleteIcon } from 'assets/icons';
import { CustomButton, CustomModal, RadioWithLabel } from 'components';
import { ButtonType } from 'components/custom-button/CustomButton';
import routesPath from 'routes/RoutesPath';
import { selectCurrentTeamJobPermissions } from 'containers/app/appSlice';
import { useAppSelector } from 'hooks';

interface EditExternalCompanyModalProps {
  open: boolean;
  onClose: VoidFn;
  jobNumber: string;
  selectedCompanyId?: string;
  onSaveClick: (companyId: string) => Promise<void>;
  isActionInProgress: boolean;
  companies: Array<{ id: string; companyName: string }>;
  onRemoveClick: VoidFn;
  isRemoveLoading: boolean;
}

interface SelectedCompany {
  id: string;
  companyName: string;
}

const EditExternalCompanyModal: FC<EditExternalCompanyModalProps> = ({
  open,
  onClose,
  jobNumber,
  selectedCompanyId,
  onSaveClick,
  isActionInProgress,
  companies,
  onRemoveClick,
  isRemoveLoading
}) => {
  const navigate = useNavigate();
  const currentJobSettingsPermissions = useAppSelector(selectCurrentTeamJobPermissions);
  const { editJobSource } = currentJobSettingsPermissions ?? {};
  const [selectedCompanyData, setSelectedCompanyData] = useState<SelectedCompany | null>(null);

  useEffect(() => {
    if (selectedCompanyId) {
      const company = companies.find(c => c.id === selectedCompanyId);
      if (company) {
        setSelectedCompanyData(company);
      }
    }
  }, [companies, selectedCompanyId]);

  const isSaveDisabled = () => selectedCompanyData?.id === selectedCompanyId;

  const handleCreateCompany = () => {
    // Store current location state to return back
    navigate(routesPath.ADD_EXTERNAL_COMPANY, {
      state: {
        returnTo: window.location.pathname,
        showExternalCompanyModal: true
      }
    });
  };

  return (
    <CustomModal open={open} closeModal={onClose} backgroundColor="bg-white" width="md:w-[510px]">
      <div className="flex items-center justify-between px-[30px] py-6">
        <div className="relative w-full">
          <h2 className="text-2xl font-semibold text-headingGray">Edit external company</h2>
          <p className="text-gray-600">{jobNumber}</p>
          <CircleCloseIcon className="absolute right-0 top-0 cursor-pointer" onClick={onClose} />
        </div>
      </div>

      <div className="mx-[30px] space-y-4 divide-y rounded-md border border-borderGray px-3 pb-4">
        <>
          {companies.length > 0 ? (
            <>
              {editJobSource && (
                <CustomButton
                  id="create-external-company"
                  onClick={handleCreateCompany}
                  type={ButtonType.SECONDARY}
                  className="w-full justify-start border-none pt-4 pl-0 shadow-none 
                hover:bg-transparent active:shadow-none"
                  childrenContainerStyles="text-[17px] font-semibold">
                  + Create external company
                </CustomButton>
              )}

              {companies.map(company => (
                <div key={company.id} className="pt-4">
                  <RadioWithLabel
                    isSelected={company.id === selectedCompanyData?.id}
                    label={company.companyName}
                    onClick={() => setSelectedCompanyData(company)}
                    labelStyle={{ fontSize: '16px' }}
                  />
                </div>
              ))}
            </>
          ) : (
            <div className="flex h-[290px] flex-col items-center justify-center py-8 text-center">
              <p className="mb-3 text-sm font-semibold text-primaryText">No external companies found</p>
              {editJobSource && (
                <CustomButton
                  id="create-external-company"
                  onClick={handleCreateCompany}
                  type={ButtonType.SECONDARY}
                  childrenContainerStyles="text-[17px] font-semibold">
                  + Create external company
                </CustomButton>
              )}
            </div>
          )}
        </>
      </div>

      <div className="mx-[30px] my-6 flex items-center justify-between space-x-3">
        {selectedCompanyId && (
          <CustomButton
            isLoading={isRemoveLoading}
            id="edit-external-company-remove-button"
            onClick={onRemoveClick}
            type={ButtonType.GHOST}
            className="flex cursor-pointer items-center gap-2 px-0"
            StartIcon={DeleteIcon}>
            <span className="text-17px font-semibold text-error">Remove</span>
          </CustomButton>
        )}
        <div className="ml-auto flex items-center gap-3">
          <CustomButton
            id="edit-external-company-cancel-button"
            onClick={onClose}
            type={ButtonType.SECONDARY}
            className="w-[100px]">
            Cancel
          </CustomButton>
          <CustomButton
            id="edit-external-company-save-button"
            onClick={() => selectedCompanyData && onSaveClick(selectedCompanyData.id)}
            disabled={isSaveDisabled()}
            isLoading={isActionInProgress && !isRemoveLoading}
            className="w-[100px]">
            Save
          </CustomButton>
        </div>
      </div>
    </CustomModal>
  );
};

export default EditExternalCompanyModal;
